import React, { useEffect, useState } from 'react';
import { AppHeader } from '../../../../app/common/sharedComponents/AppHeader';
import { HeaderContainer } from '../../../../app/common/sharedStyledComponents/MyAuditGenericStyles.styled';
import { LineHeader } from '../../MyAudit/MyAuditChecklist/MyAuditChecklist.styled';
import { SubHeaderSelector } from '../../../../app/common/sharedComponents/SubHeaderSelector';
import { IListSelector } from '../../../../app/@types/subHeaderSelector';
import { getItem } from '../../../../utils/localStorage';
import {
  getBrandAndHotelData,
  userHotelDetails,
} from '../../../../helpers/handleBrandAndHotelData';
import {
  getInitialHotelSelectionData,
  updateHotelSelection,
} from '../../../../helpers/hotelSelectorHelpers';
import { useTranslation } from 'react-i18next';
import {
  ActionPlanListContainer,
  CreateButtonContainer,
  DepartmentHeaderContainer,
  FilterContainer,
  Pill,
  PillContainer,
} from './actionPlanList.styled';
import { ActionPlanTable } from './ActionPlanTable';
import {
  IActionPlanFilter,
  IActionPlanList,
  IActionPlanListRequest,
  IActionPlanListType,
  ISort,
} from '../../../../app/@types/actionPlanList';
import { ActionPlanListType } from '../../../../enums/ActionPlanListType';
import { actionPlanList } from '../../../../requests/actionPlan';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { makeActionPlanListFilter } from '../../../../helpers/makeActionPlanListFilter';
import { Headline3 } from '../../../../app/common/typography';
import { IconButton } from '../../../../app/common/sharedComponents/IconButton';
import { FilterIcon } from '../../../../app/common/icons/FilterIcon';
import { ActionPlanListFilter } from './ActionPlanListFilter';
import { showErrorMessage } from '../../../../helpers/showMessage';
import { sentryReport } from '../../../../helpers/sentry';
import NonIdealState from 'lqa-ui/lib/Components/NonIdealState';
import { ButtonLarge } from '../../../../app/common/sharedComponents/buttons/ButtonLarge';
import { ActionPlanListSkeleton } from './ActionPlanListSkeleton';
import { orderBy } from 'lodash';
import { convertName } from '../../../../helpers/convertName';
import { UserTypeEnum } from '../../../common/enum/generic';
import {
  MYLQA_ANALYTICS_EVENTS,
  useTrackMyLqaPageView,
} from '../../../../utils/myLqaAnalytics';

export function ActionPlanList () {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useTrackMyLqaPageView(MYLQA_ANALYTICS_EVENTS.ACTION_PLAN_LIST_PAGE_VIEWED);
  const viewType: string | null = searchParams.get('viewType');
  const overDue: boolean = searchParams.get('overDue') === 'true';
  const { userData } = userHotelDetails();
  const localhostUserData = getItem('user');
  const isViewer =
    localhostUserData &&
    localhostUserData.user &&
    localhostUserData.user.userType === UserTypeEnum.noData;
  const [listSelector, setListSelector] = useState<IListSelector[]>([]);
  const [valueHeaderSelector, setValueHeaderSelector] = useState<string>();
  const [titleHeaderSelector, setTitleHeaderSelector] = useState<string>();
  const [tableData, setTableData] = useState<IActionPlanList[]>([]);
  const [actionPlanListType, setActionPlanListType] =
    useState<IActionPlanListType>(ActionPlanListType.series);

  const initializeFilterOptions: IActionPlanFilter = {
    selectedUser: [
      {
        id: userData.user.id,
        text: `${userData.user.userFirstName} ${userData.user.userLastName}`,
      },
    ],
    isSeries: actionPlanListType === ActionPlanListType.series,
    sort: '-id',
    page: 1,
    pageSize: 20,
    listFilterType: viewType,
  };
  const [filter, setFilter] = useState<IActionPlanFilter>(
    initializeFilterOptions
  );
  const [numberOfPages, setNumberOfPages] = useState<number>(0);
  const [isBottomSheetOpen, setBottomSheetOpen] = useState<boolean>(false);
  const [applyFilter, setApplyFilter] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sort, setSort] = useState<ISort>({ key: 'id', dir: 'desc' });

  function createActionPlanHandler () {
    navigate('/createActionPlan', {
      state: { alreadyAssignedUsers: [userData.user.id] },
    });
  }

  const updateFilter = (key, value) => {
    setFilter((prevFilter) => {
      const updatedFilter = {
        ...prevFilter,
        [key]: value,
      };
      return updatedFilter;
    });
  };

  const fetchTableData = () => {
    const nextPage = filter.page + 1;
    const payLoad: IActionPlanListRequest = makeActionPlanListFilter(
      { ...filter, page: nextPage },
      actionPlanListType,
      overDue
    );
    actionPlanList(payLoad)
      .then((response) => {
        setTableData((prevTableData) => [
          ...prevTableData,
          ...response.results,
        ]);
        setFilter((prevFilter) => ({ ...prevFilter, page: nextPage }));
        setNumberOfPages(response.numberOfPages);
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          alertMessage: t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_LIST'),
          method: 'fetchTableData',
          payload: {
            filter,
          },
        });
        showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_LIST'));
      });
  };

  useEffect(() => {
    setIsLoading(true);
    setTableData([]);
    const payLoad: IActionPlanListRequest = makeActionPlanListFilter(
      filter,
      actionPlanListType,
      overDue
    );
    actionPlanList(payLoad)
      .then((response) => {
        setNumberOfPages(response.numberOfPages);
        setTableData(response.results);
        setIsLoading(false);
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          alertMessage: t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_LIST'),
          payload: {
            payLoad,
          },
        });
        showErrorMessage(t('COMMON.ALERT_MESSAGES.UNABLE_TO_GET_LIST'));
        setIsLoading(false);
      });
  }, [actionPlanListType, applyFilter, listSelector]);

  useEffect(() => {
    setTableData((tableData) => {
      return orderBy(tableData, sort.key, [sort.dir]);
    });
  }, [sort]);

  function updateSorting (key: string) {
    setSort((prevSort) => ({
      key,
      dir: prevSort.key === key && prevSort.dir === 'asc' ? 'desc' : 'asc',
    }));
  }

  useEffect(() => {
    const allHotels = getBrandAndHotelData();
    const { hotelName, brandName, hotelsWithSelection } =
      getInitialHotelSelectionData(allHotels);

    setListSelector(hotelsWithSelection);
    setTitleHeaderSelector(brandName);
    setValueHeaderSelector(hotelName);
  }, []);

  function clickHotelSelection (
    hotelId: string | number,
    clientId: string | number,
    brandId: string | number
  ) {
    const { updatedHotelList, selectedHotel } = updateHotelSelection(
      listSelector,
      hotelId,
      clientId,
      brandId
    );

    if (selectedHotel) {
      setTitleHeaderSelector(selectedHotel.brandName);
      setValueHeaderSelector(selectedHotel.name);
    }

    setListSelector(updatedHotelList);
  }

  function handleFilter () {
    if (
      (filter.startDate || filter.endDate) &&
      !(filter.startDate && filter.endDate)
    ) {
      showErrorMessage(
        t('COMMON.ALERT_MESSAGES.PLEASE_SELECT_VALID_DATE_RANGE')
      );
      return;
    }
    setApplyFilter(!applyFilter);
    setBottomSheetOpen(false);
  }

  function handleClearFilter () {
    setFilter(initializeFilterOptions);
    setNumberOfPages(0);
    setBottomSheetOpen(false);
    setApplyFilter(!applyFilter);
  }

  return (
    <>
      <HeaderContainer>
        <AppHeader
          title={t('COMMON.ACTION_PLAN.TITLE')}
          navigateTo="/myactions"
        />
        <LineHeader />
        <SubHeaderSelector
          title={titleHeaderSelector}
          subTitle={valueHeaderSelector}
          listSelector={listSelector}
          clickSelectSection={clickHotelSelection}
        />
      </HeaderContainer>
      <ActionPlanListContainer>
        <PillContainer>
          <Pill
            checked={actionPlanListType === ActionPlanListType.series}
            onClick={() => {
              setActionPlanListType(ActionPlanListType.series);
              updateFilter('isSeries', true);
              updateFilter('page', 1);
            }}
          >
            {t('COMMON.ACTION_PLAN.SERIES_TITLE')}
          </Pill>
          <Pill
            checked={actionPlanListType === ActionPlanListType.individual}
            onClick={() => {
              setActionPlanListType(ActionPlanListType.individual);
              updateFilter('isSeries', undefined);
              updateFilter('page', 1);
            }}
          >
            {t('COMMON.ACTION_PLAN.INDIVIDUAL_ACTION_PLANS')}
          </Pill>
        </PillContainer>
        <DepartmentHeaderContainer>
          {filter.listFilterType && (
            <Headline3>
              {convertName(filter.listFilterType)}
              {t('COMMON.ACTION_PLAN.TITLE')}
            </Headline3>
          )}
          <FilterContainer>
            <IconButton
              title={t('COMMON.MY_AUDIT.BUTTON.FILTER')}
              onClick={() => setBottomSheetOpen(true)}
              rightIcon={<FilterIcon />}
            />
          </FilterContainer>
        </DepartmentHeaderContainer>
        {isLoading && <ActionPlanListSkeleton />}
        {tableData && tableData.length > 0 && (
          <ActionPlanTable
            tableData={tableData}
            updateSorting={updateSorting}
            viewType={filter.listFilterType || viewType}
            actionPlanListType={actionPlanListType}
            fetchTableData={fetchTableData}
            filter={filter}
            hasMore={numberOfPages > filter.page}
          />
        )}
        {!isLoading && tableData && tableData.length === 0 && (
          <>
            <NonIdealState
              title={t(
                'COMMON.ACTION_PLAN.ALERT_MESSAGES.NO_ACTION_PLAN_AVAILABLE'
              )}
              description=""
              icon="illustration-nonideal"
              iconOffset={[10, -20]}
            />
            {!isViewer && (
              <CreateButtonContainer>
                <ButtonLarge
                  title={t(
                    'COMMON.MY_ACTIONS.BOTTOM_SHEET.BUTTONS.CREATE_ACTION_PLAN'
                  )}
                  onClick={() => {
                    createActionPlanHandler();
                  }}
                />
              </CreateButtonContainer>
            )}
          </>
        )}
      </ActionPlanListContainer>
      {isBottomSheetOpen && (
        <ActionPlanListFilter
          isBottomSheetOpen={isBottomSheetOpen}
          setBottomSheetOpen={setBottomSheetOpen}
          handleFilter={handleFilter}
          clearFilter={handleClearFilter}
          updateFilter={updateFilter}
          filter={filter}
        />
      )}
    </>
  );
}
